import React from "react";
import { motion } from "framer-motion";

// Product Images
const IMAGES = {
  royalPlay: "https://images.unsplash.com/photo-1758448755778-90ebf4d0f1e7?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  texture: "https://images.unsplash.com/photo-1763657157242-12572ee58928?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  wallpaper: "https://images.unsplash.com/photo-1747228284879-c9019bc70ea2?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  putty: "https://images.unsplash.com/photo-1666706566990-d0072aa28e92?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  floor: "https://images.unsplash.com/photo-1622250544703-997d69d856e3?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  gloss: "https://images.unsplash.com/photo-1758448511320-05d7d28f4298?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
};

export const ProductsSection = () => {
  return (
    <section id="products" className="py-16 sm:py-20 lg:py-28 paint-splash-bg" data-testid="products-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="text-center mb-10 sm:mb-14">
          <motion.span 
            initial={{ opacity: 0, y: 15 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }} 
            className="text-purple-600 font-semibold tracking-wider uppercase text-xs sm:text-sm"
          >
            Our Products
          </motion.span>
          <motion.h2 
            initial={{ opacity: 0, y: 15 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }} 
            transition={{ delay: 0.1 }} 
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mt-2 sm:mt-3"
          >
            Premium Finishes & <span className="text-purple-600">Coatings</span>
          </motion.h2>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
          {/* Royal Play Designs - Large Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }} 
            className="bento-item col-span-2 row-span-2 relative overflow-hidden" 
            data-testid="product-royal-play"
          >
            <img 
              src={IMAGES.royalPlay} 
              alt="Royal Play Designs" 
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
            <div className="relative h-full min-h-[200px] sm:min-h-[280px] lg:min-h-[320px] p-4 sm:p-6 flex flex-col justify-end">
              <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-1 sm:mb-2">Royal Play Designs</h3>
              <p className="text-white/90 text-xs sm:text-sm">Premium decorative finishes for luxury interiors.</p>
            </div>
          </motion.div>

          {/* Texture Paints */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }} 
            transition={{ delay: 0.1 }} 
            className="bento-item relative overflow-hidden" 
            data-testid="product-texture"
          >
            <img 
              src={IMAGES.texture} 
              alt="Texture Paints" 
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <div className="relative h-full min-h-[120px] sm:min-h-[140px] p-4 sm:p-5 flex flex-col justify-end">
              <h3 className="text-base sm:text-lg font-bold text-white mb-1">Texture Paints</h3>
              <p className="text-white/90 text-xs">Stunning wall textures</p>
            </div>
          </motion.div>

          {/* Wallpapers - Tall Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }} 
            transition={{ delay: 0.15 }} 
            className="bento-item row-span-2 relative overflow-hidden" 
            data-testid="product-wallpapers"
          >
            <img 
              src={IMAGES.wallpaper} 
              alt="Wallpapers" 
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <div className="relative h-full min-h-[250px] sm:min-h-[290px] p-4 sm:p-5 flex flex-col justify-end">
              <h3 className="text-base sm:text-lg font-bold text-white mb-1">Wallpapers</h3>
              <p className="text-white/90 text-xs">Designer wallpapers for every style</p>
            </div>
          </motion.div>

          {/* Wall Putty */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }} 
            transition={{ delay: 0.2 }} 
            className="bento-item relative overflow-hidden" 
            data-testid="product-putty"
          >
            <img 
              src={IMAGES.putty} 
              alt="Wall Putty" 
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <div className="relative h-full min-h-[120px] sm:min-h-[140px] p-4 sm:p-5 flex flex-col justify-end">
              <h3 className="text-base sm:text-lg font-bold text-white mb-1">Wall Putty</h3>
              <p className="text-white/90 text-xs">Smooth base for finishes</p>
            </div>
          </motion.div>

          {/* Floor Paints */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }} 
            transition={{ delay: 0.25 }} 
            className="bento-item relative overflow-hidden" 
            data-testid="product-floor"
          >
            <img 
              src={IMAGES.floor} 
              alt="Floor Paints" 
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <div className="relative h-full min-h-[120px] sm:min-h-[140px] p-4 sm:p-5 flex flex-col justify-end">
              <h3 className="text-base sm:text-lg font-bold text-white mb-1">Floor Paints</h3>
              <p className="text-white/90 text-xs">Durable floor coatings</p>
            </div>
          </motion.div>

          {/* Gloss Polish */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }} 
            transition={{ delay: 0.3 }} 
            className="bento-item relative overflow-hidden" 
            data-testid="product-gloss"
          >
            <img 
              src={IMAGES.gloss} 
              alt="Gloss Polish" 
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <div className="relative h-full min-h-[120px] sm:min-h-[140px] p-4 sm:p-5 flex flex-col justify-end">
              <h3 className="text-base sm:text-lg font-bold text-white mb-1">Gloss Polish</h3>
              <p className="text-white/90 text-xs">High-shine finishes</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
